import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useI18n } from '../i18n/useI18n'
import { useStaffAuth } from '../store/useStaffAuth'
import { useStoreOwner } from '../store/useStoreOwner'
import { useEcVendorTerms } from '../hooks/useEcVendorTerms'
import { ApiError } from '../services/api'
import MerchantTermsBody from './MerchantTermsBody'

/**
 * EC 出店規約の未同意ブロック (#1710)。加盟店規約の StoreTermsGate と同じ型で、
 * **EC の出店者としてリンクされた店舗を持つオーナーにだけ**出す。
 *
 * 加盟店規約と違い**閉じられる** (「あとで読む」)。EC 出店規約は EC の取引条件で、
 * 同意していなくても PAY のレジや注文は動かなければならない。閉じた場合は
 * EC 連携の操作 (商品タブの EC 商品リンク) が同意まで出ないだけで、次に
 * 店舗画面を開いたときにまた出る (閉じたことはこのタブの間だけ覚える)。
 *
 * 同意は常に**最後まで読んだ上での明示的なタップ**。精算からの控除への同意を
 * 含む契約なので、版によらず継続利用によるみなし同意にはしない。
 */
// 「あとで読む」の記憶は **オーナーと版ごと** に持つ。端末を共有するオーナーが
// 替わったときや、規約が改定されたときに、前の「あとで」で黙ってしまわないように。
const dismissKey = (uid: string, version: string) => `ecVendorTermsGateDismissed:${uid}:${version}`

export default function EcVendorTermsGate() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const staffSession = useStaffAuth((s) => s.session)
  const uid = useStoreOwner((s) => s.firebaseUser?.uid) ?? ''
  const { terms, loading, agree, reload, needsAgreement } = useEcVendorTerms()
  const [submitting, setSubmitting] = useState(false)
  const [failed, setFailed] = useState(false)
  const [stale, setStale] = useState(false)
  const [scrolledToEnd, setScrolledToEnd] = useState(false)
  const [dismissedKey, setDismissedKey] = useState<string | null>(null)

  if (staffSession) return null
  if (loading || !terms || !needsAgreement) return null
  const key = dismissKey(uid, terms.currentVersion)
  let dismissed = dismissedKey === key
  if (!dismissed) {
    try {
      dismissed = sessionStorage.getItem(key) === '1'
    } catch {
      dismissed = false
    }
  }
  if (dismissed) return null

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setScrolledToEnd(true)
  }
  const unlockIfFullyVisible = (el: HTMLDivElement | null) => {
    if (el && el.scrollHeight <= el.clientHeight + 24) setScrolledToEnd(true)
  }

  const onAgree = async () => {
    setSubmitting(true)
    setFailed(false)
    try {
      await agree()
    } catch (e) {
      // 409 = 表示中の版が古い。読み直さないと同じ古い版を送り続ける
      if (e instanceof ApiError && e.status === 409) {
        setScrolledToEnd(false)
        setStale(true)
        await reload()
      } else {
        setFailed(true)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const onDismiss = () => {
    try {
      sessionStorage.setItem(key, '1')
    } catch {
      // ignore storage errors
    }
    setDismissedKey(key)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center">
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-2xl bg-white sm:rounded-2xl">
        <div className="border-b border-gray-100 px-5 pb-3 pt-5">
          <p className="font-heading text-base font-bold text-text-primary">
            {terms.agreedVersion ? t('storeTerms.ec.gate.titleUpdated') : t('storeTerms.ec.gate.titleFirst')}
          </p>
          <p className="mt-1 font-body text-[13px] text-text-secondary">{t('storeTerms.ec.gate.lead')}</p>
          <p className="mt-1 font-body text-[11px] text-text-tertiary">
            {t('storeTerms.version')}: {terms.currentVersion}
            {terms.agreedVersion ? ` （${t('storeTerms.agreedVersion')}: ${terms.agreedVersion}）` : ''}
          </p>
        </div>

        <div ref={unlockIfFullyVisible} onScroll={onScroll} className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
          <MerchantTermsBody text={terms.text} />
        </div>

        <div className="border-t border-gray-100 px-5 pb-5 pt-3">
          {failed && <p className="mb-2 font-body text-[12px] text-yokoso-red">{t('storeTerms.gate.failed')}</p>}
          {stale && <p className="mb-2 font-body text-[12px] text-text-secondary">{t('storeTerms.gate.stale')}</p>}
          {!scrolledToEnd && (
            <p className="mb-2 font-body text-[12px] text-text-tertiary">{t('storeTerms.gate.scrollToEnd')}</p>
          )}
          <button
            onClick={() => void onAgree()}
            disabled={submitting || !scrolledToEnd}
            className="tap-effect w-full rounded-xl bg-navy py-3 font-body text-sm font-medium text-white disabled:opacity-40"
          >
            {submitting ? t('storeTerms.gate.submitting') : t('storeTerms.gate.agree')}
          </button>
          <div className="mt-2 flex items-center justify-between">
            <button onClick={onDismiss} className="tap-effect font-body text-[12px] text-text-secondary underline">
              {t('storeTerms.ec.gate.later')}
            </button>
            <button
              onClick={() => { onDismiss(); navigate('/store/ec-terms') }}
              className="tap-effect font-body text-[12px] text-navy underline"
            >
              {t('storeTerms.ec.gate.openPage')}
            </button>
          </div>
          <p className="mt-2 text-center font-body text-[11px] text-text-tertiary">{t('storeTerms.ec.gate.recordNote')}</p>
        </div>
      </div>
    </div>
  )
}
